import React from "react";
import axios from "axios";

import { apiClient } from "../../api/apiClient";
import { useQuery } from "@tanstack/react-query";
import {
  MRT_ColumnFiltersState,
  MRT_PaginationState,
  MRT_SortingState,
} from "material-react-table";
import {
  type RiskmapQueryResult,
  type Riskmap,
  RiskmapTableUpdateArg,
} from "../../types/riskmap";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-toastify";

export function useCreateRiskmap({ table }: RiskmapTableUpdateArg) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (riskmap: Riskmap) => {
      try {
        const response = await apiClient.post("/api/riskmap/", riskmap);

        table.setCreatingRow(null);
        toast.success(`Riskmap ${riskmap.name} created successfully`);
        return response.data;
      } catch (error) {
        if (axios.isAxiosError(error) && error.response) {
          const errorData = error.response.data;
          for (const key in errorData) {
            if (Array.isArray(errorData[key])) {
              const errorMessage = errorData[key].join(", ");
              toast.error(`${key.toUpperCase()}: ${errorMessage}`);
            } else {
              toast.error(`${key.toUpperCase()}: ${errorData[key]}`);
            }
          }
        } else {
          toast.error(`Error creating riskmap: ${error}`);
        }
      }
    },
    onSuccess: (newRiskmap: Riskmap) => {
      queryClient.setQueryData<Riskmap[]>(["riskmap"], (preRiskmaps = []) => [
        ...preRiskmaps,
        newRiskmap,
      ]);
    },
    onSettled: () => queryClient.invalidateQueries({ queryKey: ["riskmap"] }),
  });
}

export function useDeleteRiskmap() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (riskmapId: string) => {
      try {
        await apiClient.delete(`/api/riskmap/${riskmapId}`);
        toast.success("Riskmap successfully deleted");
      } catch (error) {
        toast.error("Failed to delete the riskmap");
      }
    },
    onMutate: (riskmapId: string) => {
      queryClient.setQueryData(["riskmap"], (prevRiskmaps: any) =>
        prevRiskmaps?.filter((riskmap: Riskmap) => riskmap.id !== riskmapId)
      );
    },
    onSettled: () => queryClient.invalidateQueries({ queryKey: ["riskmap"] }),
  });
}

export function useSearchFindings({ q }: { q: string }) {
  return useQuery<RiskmapQueryResult[]>({
    queryKey: ["riskmap", q],
    queryFn: async () => {
      try {
        const url = `/api/riskmap?search=${q}`;

        const response = await apiClient.get(url);

        return [
          {
            riskmap: response.data.results as Riskmap[],
            rowCount: response.data.count,
          },
        ];
      } catch (error) {
        //
        return [{ riskmap: [], rowCount: 0 }];
      }
    },
    refetchOnWindowFocus: true,
  });
}

export const useExportRiskmaps = (columnFilters: any, sorting: any) => {
  const exportRiskmaps = async () => {
    try {
      const response = await apiClient.post("/api/riskmap/export/", {
        params: {
          filters: JSON.stringify(columnFilters),
          sorting: JSON.stringify(sorting),
        },
        responseType: "blob",
      });

      const blob = new Blob([response.data], { type: "text/csv" });
      const downloadUrl = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = downloadUrl;

      const now = new Date();
      const dateString = now.toISOString().split("T")[0];
      const timestamp = now.getTime();
      const filename = `Riskmap-${dateString}-${timestamp}.csv`;

      a.download = filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(downloadUrl);
      a.remove();

      toast.success(`${filename} downloaded successfully`);
    } catch (error) {
      toast.error(`Failed to download file : ${error}`);
    }
  };

  return exportRiskmaps;
};

export function useGetAllFindings() {
  return useQuery<RiskmapQueryResult[]>({
    queryKey: ["all_riskmap"],
    queryFn: async () => {
      try {
        const url = `/api/riskmap`;

        const response = await apiClient.get(url);
        return [
          {
            riskmap: response.data.results as Riskmap[],
            rowCount: response.data.count,
          },
        ];
      } catch (error) {
        //
        return [{ riskmap: [], rowCount: 0 }];
      }
    },
    refetchOnWindowFocus: false,
  });
}

export function useGetRiskmap({
  pagination,
  columnFilters,
  sorting,
  globalFilter,
  exportCsv,
}: {
  pagination: MRT_PaginationState;
  columnFilters: MRT_ColumnFiltersState;
  sorting: MRT_SortingState;
  globalFilter: string | undefined;
  exportCsv?: boolean;
}) {
  return useQuery<RiskmapQueryResult[]>({
    queryKey: [
      "riskmap",
      pagination,
      columnFilters,
      sorting,
      globalFilter,
      exportCsv,
    ],
    queryFn: async () => {
      try {
        const url = `/api/riskmap`;
        const params = new URLSearchParams({
          page: (pagination.pageIndex + 1).toString(),
          limit: pagination.pageSize.toString(),
          filters: JSON.stringify(columnFilters),
          sorting: JSON.stringify(sorting),
          globalFilter: globalFilter ?? "",
        });

        const response = await apiClient.get(url, { params });

        return [
          {
            riskmap: response.data.results as Riskmap[],
            rowCount: response.data.count,
          },
        ];
      } catch (error) {
        //
        return [{ riskmap: [], rowCount: 0 }];
      }
    },
    refetchOnWindowFocus: true,
  });
}

export function useGetRiskmapDetail({ name }: { name: string }) {
  return useQuery<Riskmap[]>({
    queryKey: ["riskmap"],
    queryFn: async () => {
      try {
        const url = `/api/riskmap`;
        const params = new URLSearchParams({
          name: name,
        });

        const response = await apiClient.get(url, { params });

        return response.data.results;
      } catch (error) {
        //
        return {};
      }
    },
    refetchOnWindowFocus: true,
  });
}

export function useUpdateRiskmap({ table }: RiskmapTableUpdateArg) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (riskmap: Riskmap) => {
      try {
        await apiClient.patch(`/api/riskmap/${riskmap.id}/`, riskmap);

        table.setEditingRow(null);
        toast.success(`Riskmap ${riskmap.name} updated successfully`);
      } catch (error) {
        if (axios.isAxiosError(error) && error.response) {
          const errorData = error.response.data;
          for (const key in errorData) {
            if (Array.isArray(errorData[key])) {
              const errorMessage = errorData[key].join(", ");
              toast.error(`${key.toUpperCase()}: ${errorMessage}`);
            } else {
              toast.error(`${key.toUpperCase()}: ${errorData[key]}`);
            }
          }
        } else {
          toast.error(`Error updating riskmap ${error}`);
        }
      }
    },

    onMutate: (newRiskmapInfo: Riskmap) => {
      queryClient.setQueryData(["riskmap"], (prevRiskmaps: any) =>
        prevRiskmaps?.map((prevRiskmap: Riskmap) =>
          prevRiskmap.id === newRiskmapInfo.id ? newRiskmapInfo : prevRiskmap
        )
      );
    },
    onSettled: () => queryClient.invalidateQueries({ queryKey: ["riskmap"] }),
  });
}

export const useDeleteRiskmaps = (
  columnFilters: any,
  sorting: any,
  rowCount: number
) => {
  const deleteRiskmaps = async () => {
    try {
      await apiClient.post("/api/riskmap/delete/", {
        params: {
          filters: JSON.stringify(columnFilters),
          sorting: JSON.stringify(sorting),
        },
      });

      toast.success(`Deleted ${rowCount} rows successfully`);
    } catch (error) {
      toast.error("Failed to delete rows");
    } finally {
      setTimeout(() => {
        window.location.reload();
      }, 1000);
    }
  };

  return deleteRiskmaps;
};

export const useApplyRiskmap = () => {
  const applyRiskmap = async () => {
    try {
      await apiClient.get("/api/riskmap/applyall/", {
        params: {},
      });

      toast.success(`Applied successfully`);
    } catch (error) {
      toast.error("Failed to apply");
    } finally {
      /* setTimeout(() => {
                 window.location.reload();
             }, 1000); */
    }
  };

  return applyRiskmap();
};
